import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as api from "../../../api/apiConnector.js";
import * as utils from "../../../components/utils.js";
import {
  scrollToBottom,
  calculateHeaderHeight,
} from "../../../components/ui_utils.js";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import * as labels from "../../../components/localized/labels.js";
import * as breadcrumb from "../../../components/ui/breadcrumb.js";

const ChatContext = createContext();

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children, currentUser }) => {
  const { id = null, folder_id } = useParams();
  const { t } = useTranslation("labels");

  // File and folder state
  const [fileInfo, setFileInfo] = useState();
  const [folderInfo, setFolderInfo] = useState();
  const [path, setPath] = useState();

  // Chat state
  const [history, setHistory] = useState([]);
  const [toAddInTheHistory, setToAddInTheHistory] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isQueryEmpty, setIsQueryEmpty] = useState(true);

  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const [showFolderDocuments, setShowFolderDocuments] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);

  const [folderFiles, setFolderFiles] = useState([]);
  const [isFolderFilesLoading, setIsFolderFilesLoading] = useState(false);

  // Chat history and grounding
  const [chatHistoryNodes, setChatHistoryNodes] = useState({
    files: [],
    folders: [],
  });
  const [groundingFolders, setGroundingFolders] = useState([]);
  const [selectedGroundingFolders, setSelectedGroundingFolders] = useState([]);

  // Refs
  const messagesEndRef = useRef(null);
  const fileListRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Auto-scroll effect
  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [history, toAddInTheHistory]);

  // Header height effect
  useEffect(() => {
    setHeaderHeight(calculateHeaderHeight());
    window.addEventListener("resize", calculateHeaderHeight);

 
    return () => {
      window.removeEventListener("resize", calculateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    if (folderInfo) {
      setIsFolderFilesLoading(true);
      getFolderFiles(folderInfo.Id).then((files) => {
        setFolderFiles(files);
        setIsFolderFilesLoading(false);
      });
    }
  }, [folderInfo]);

  // Reset selected grounding folders when available folders change
  useEffect(() => {
    if (groundingFolders.length > 0) {
      // Filter out any selected folders that are no longer available
      setSelectedGroundingFolders((prev) =>
        prev.filter((selectedId) =>
          groundingFolders.some((folder) => folder.Id === selectedId)
        )
      );
    } else {
      // Clear selection if no folders are available
      setSelectedGroundingFolders([]);
    }
  }, [groundingFolders]);

  // API functions
  const getFile = async (id) => {
    const result = await api.sendMessage("dms/getFileInfo", {
      user: currentUser(),
      body: { id },
    });
    return result;
  };

  const getFolder = async (id) => {
    const result = await api.sendMessage("dms/getFolderInfo", {
      user: currentUser(),
      body: { id },
    });
    return result;
  };

  const getFolderFiles = async (id) => {
    const result = await api.sendMessage("dms/getFolder", {
      user: currentUser(),
      body: { id },
    });
    if (result.content && result.content.files) {
      return result.content.files.map((f) => ({
        id: f.Id,
        name: f.Name,
        type: f.MimeType,
      }));
    }
    return [];
  };

  const getUserChatNodes = async () => {
    const result = await api.sendMessage("dms/getUserChatNodes", {
      user: currentUser(),
    });
    if (result.success) {
      return result.data;
    }
    return { files: [], folders: [] };
  };

  const getPath = async (id) => {
    let _dbPath = await api.sendMessage("dms/getFolderPath", {
      user: currentUser(),
      body: { id },
    });

    if (_dbPath?.folders) {
      let _path = _dbPath.folders.map((f) => ({
        name: f.Name,
        link: `/dms/${f.Id}`,
      }));

      _path = [
        {
          link: "/dms",
          name: "DMS",
        },
      ].concat(_path);

      _path = [
        {
          link: "/",
          name: "Home",
        },
      ].concat(_path);

      _path.push({
        link: "/dms/${file.Id}/chat",
        name: "Chat",
      });
      return _path;
    }
    return [];
  };

  // Chat message parsing and handling
  const _normalize_chat = () => {
    if (window.client && window.client.html) {
      if (window.client.html.startsWith("```html")) {
        window.client.html = window.client.html.substring(7);
      }
      if (
        window.client.html.endsWith("``` ") &&
        window.client.html.length > 4
      ) {
        window.client.html = window.client.html.substring(
          0,
          window.client.html.length - 4
        );
      }
      if (
        window.client.html.includes("```html") &&
        window.client.html.endsWith("```")
      ) {
        const startIndex = window.client.html.indexOf("```html") + 7;
        window.client.html = window.client.html.substring(
          startIndex,
          window.client.html.length
        );
        window.client.html = window.client.html.substring(
          0,
          window.client.html.length - 3
        );
      }
    }
  };

  const _parse_html = () => {
    const _div = document.getElementById("current-streaming-answer");
    if (_div) {
      _div
        .querySelectorAll(".table")
        .forEach((el) => el.classList.remove("table"));
    }
  };

  const _sendMessage = async (message) => {
    if (window.client?.id) {
      message.id = window.client.id;
      message.tutor = document.getElementById("ai-tutor")?.value || "AI_PLAN";

      // Support for multiple grounding folders (new format)
      if (selectedGroundingFolders.length > 0) {
        message.grounding_folders = selectedGroundingFolders;
      } else {
        // Backward compatibility: check for legacy single folder selection
        const legacyGrounding = document.getElementById("ai-grounding")?.value;
        if (legacyGrounding) {
          message.grounding_folders = [legacyGrounding];
        } else {
          message.grounding_folders = [];
        }
      }
    }

    const result = await api.sendMessage("dms/sendChatMessage", {
      user: currentUser(),
      body: { message },
    });
  };

  const _parseMessage = (message) => {
    switch (message.type) {
      case "stopping":
        {
          window.document.location.href = "/";
        }
        break;
      case "still_there":
        {
          withReactContent(Swal)
            .fire({
              title: t("stillThere", { ns: "labels" }),
              icon: "question",
              text: t("stillThereQuestion", { ns: "labels" }),
              showConfirmButton: true,
            })
            .then(async (result) => {
              if (result.isConfirmed) {
                _sendMessage({ type: "ping" });
              } else {
                window.document.location.href = "/";
              }
            });
        }
        break;
      case "activity":
        {
          switch (message.ai_source?.action) {
            case "file_progress":
              {
                // Handle file progress - could show in a toast or status
              }
              break;
            case "answer_starting":
              {
                window.client.starting = true;
                setIsStreaming(true);
              }
              break;
            default: {
              // Handle other activities
            }
          }
        }
        break;
      case "chat":
        {
          if (window.client.starting) {
            window.client.html = "";
            window.client.starting = false;
          }

          window.client.html += message.message;
          _normalize_chat();

          // Update the streaming answer display
          const streamingDiv = document.getElementById(
            "current-streaming-answer"
          );
          if (streamingDiv) {
            streamingDiv.innerHTML = window.client.html;
            _parse_html();
          }

          if (message.done) {
            setToAddInTheHistory({
              id: message.id,
              question: message.prompt,
              answer: message.answer,
            });
            console.log("message id", message.id);
            setIsStreaming(false);
          }
        }
        break;
      case "wellcome":
        {
          window.client.id = message.id;
          _sendMessage({
            type: "authenticate",
            token: currentUser().token,
            file_id: id,
            folder_id: folder_id,
          });
        }
        break;
      case "authenticated":
        {
          if (message.prompts) {
            message.prompts.sort(function (a, b) {
              return a.ItemCreated - b.ItemCreated;
            });
            const history = message.prompts.map((x) => ({
              id: x.Id,
              question: x.Prompt,
              answer: x.Answer,
              like: x.Like,
            }));

            setGroundingFolders(utils.clone(message.grounding_folders));
            setHistory(utils.clone(history));
          }
        }
        break;
      default:
        break;
    }
  };

  const startChat = async () => {
    if (!window.client) {
      const _client = new window.EventSource(
        `${process.env.REACT_APP_API_URL}/dms/startChat`
      );
      _client.onmessage = (e) => {
        _parseMessage(JSON.parse(e.data));
      };

      window.client = {
        emitter: _client,
        html: "",
      };
      return _client;
    }
  };

  // Initialize chat data
  const initializeChat = async () => {
    // Get user chat nodes
    const nodes = await getUserChatNodes();
    setChatHistoryNodes(nodes);

    // Get file or folder info
    if (id) {
      const _file = await getFile(id);
      if (_file.file) {
        setFileInfo(_file.file);
        const _path = await getPath(_file.parentId);
        setPath(_path);
        breadcrumb.set(_path);
        const _chat = await startChat();
      } else {
        // Show error if file not found
        withReactContent(Swal)
          .fire({
            title: labels.getText()["ERROR"](),
            icon: "warning",
            text: labels.getText()["FILE_NOT_FOUND"](),
            showConfirmButton: true,
            confirmButtonText: labels.getText()["CLOSE"](),
          })
          .then(async (result) => {
            if (result.isConfirmed) {
              withReactContent(Swal).close();
              window.location.href = "/dms";
            }
          });
      }
    } else {
      // If we have a folder ID, load folder info
      const _folder = await getFolder(folder_id);
      if (_folder) {
        setFolderInfo(_folder.folder);
        const _path = await getPath(folder_id);
        setPath(_path);
        breadcrumb.set(_path);
        const _chat = await startChat();
      }
    }
  };

  // Utility functions for grounding folders
  const toggleGroundingFolder = (folderId) => {
    setSelectedGroundingFolders((prev) =>
      prev.includes(folderId)
        ? prev.filter((id) => id !== folderId)
        : [...prev, folderId]
    );
  };

  const selectAllGroundingFolders = () => {
    setSelectedGroundingFolders(groundingFolders.map((folder) => folder.Id));
  };

  const clearGroundingFolders = () => {
    setSelectedGroundingFolders([]);
  };

  // Context value
  const contextValue = {
    // State
    fileInfo,
    setFileInfo,
    folderInfo,
    setFolderInfo,
    path,
    setPath,
    history,
    setHistory,
    toAddInTheHistory,
    setToAddInTheHistory,
    currentQuestion,
    setCurrentQuestion,
    isStreaming,
    setIsStreaming,
    isQueryEmpty,
    setIsQueryEmpty,
    showSettings,
    setShowSettings,
    showHistorySidebar,
    setShowHistorySidebar,
    showFolderDocuments,
    setShowFolderDocuments,
    showTagDropdown,
    setShowTagDropdown,
    selectedIndex,
    setSelectedIndex,
    headerHeight,
    setHeaderHeight,
    chatHistoryNodes,
    setChatHistoryNodes,
    groundingFolders,
    setGroundingFolders,
    selectedGroundingFolders,
    setSelectedGroundingFolders,
    folderFiles,
    setFolderFiles,
    isFolderFilesLoading,

    // Refs
    messagesEndRef,
    fileListRef,
    chatContainerRef,

    // Functions
    getFile,
    getFolder,
    getFolderFiles,
    getUserChatNodes,
    getPath,
    _sendMessage,
    _parseMessage,
    startChat,
    initializeChat,
    _normalize_chat,
    _parse_html,

    // Grounding folder utilities
    toggleGroundingFolder,
    selectAllGroundingFolders,
    clearGroundingFolders,

    // Params
    id,
    folder_id,
  };

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};
